import React from 'react';
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  TrendingUp,
  Award,
  Target,
  Coffee
} from 'lucide-react';

const MeetingInsights = ({ meetings, userType, meetingsThisWeekCount }) => {
  // Calculate insights based on meeting data
  const calculateInsights = () => {
    if (!meetings || meetings.length === 0) {
      return {
        averageMeetingTime: 'N/A',
        mostPopularTimeSlot: 'N/A',
        topRestaurant: 'N/A',
        completionRate: 0,
        avgMeetingsPerWeek: 0,
        mostProductiveDay: 'N/A'
      };
    }

    // Time slot analysis
    const timeSlots = {};
    meetings.forEach(meeting => {
      const hour = parseInt(meeting.time?.split(':')[0] || '12');
      const timeSlot = hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening';
      timeSlots[timeSlot] = (timeSlots[timeSlot] || 0) + 1;
    });
    
    const mostPopularTimeSlot = Object.keys(timeSlots).reduce((a, b) => 
      timeSlots[a] > timeSlots[b] ? a : b, 'Afternoon'
    );

    // Restaurant analysis
    const restaurants = {};
    meetings.forEach(meeting => {
      if (meeting.restaurant) {
        restaurants[meeting.restaurant] = (restaurants[meeting.restaurant] || 0) + 1;
      }
    });
    
    const topRestaurant = Object.keys(restaurants).length > 0 ? 
      Object.keys(restaurants).reduce((a, b) => restaurants[a] > restaurants[b] ? a : b, 'N/A') : 'N/A';

    // Completion rate
    const confirmedMeetings = meetings.filter(m => m.status === 'confirmed').length;
    const completionRate = meetings.length > 0 ? (confirmedMeetings / meetings.length) * 100 : 0;

    // Day of week analysis
    const dayStats = {};
    meetings.forEach(meeting => {
      const dayName = new Date(meeting.date).toLocaleDateString('en-US', { weekday: 'long' });
      dayStats[dayName] = (dayStats[dayName] || 0) + 1;
    });
    
    const mostProductiveDay = Object.keys(dayStats).length > 0 ? 
      Object.keys(dayStats).reduce((a, b) => dayStats[a] > dayStats[b] ? a : b, 'Monday') : 'Monday';

    // Weekly average
    const weekSpan = meetings.length > 0 ? 
      Math.ceil((new Date() - new Date(Math.min(...meetings.map(m => new Date(m.date))))) / (7 * 24 * 60 * 60 * 1000)) : 1;
    const avgMeetingsPerWeek = Math.round(meetings.length / Math.max(weekSpan, 1));

    return {
      averageMeetingTime: '1.2 hrs', // This would be calculated from actual meeting duration data
      mostPopularTimeSlot,
      topRestaurant,
      completionRate: Math.round(completionRate),
      avgMeetingsPerWeek,
      mostProductiveDay
    };
  };

  const insights = calculateInsights();

  // Get performance metrics based on user type
  const getPerformanceMetrics = () => {
    if (userType === 'sales') {
      return [
        {
          icon: <Target className="w-5 h-5 text-blue-600" />,
          label: 'Success Rate',
          value: `${insights.completionRate}%`,
          description: 'Meeting confirmation rate'
        },
        {
          icon: <Clock className="w-5 h-5 text-green-600" />,
          label: 'Peak Time',
          value: insights.mostPopularTimeSlot,
          description: 'Most booked time slot'
        },
        {
          icon: <Calendar className="w-5 h-5 text-purple-600" />,
          label: 'Best Day',
          value: insights.mostProductiveDay,
          description: 'Most meetings scheduled'
        },
        {
          icon: <MapPin className="w-5 h-5 text-orange-600" />,
          label: 'Top Venue',
          value: insights.topRestaurant.length > 20 ? 
            insights.topRestaurant.substring(0, 17) + '...' : 
            insights.topRestaurant,
          description: 'Most popular restaurant'
        }
      ];
    } else {
      return [
        {
          icon: <Users className="w-5 h-5 text-blue-600" />,
          label: 'Meeting Rate',
          value: `${insights.avgMeetingsPerWeek}/wk`,
          description: 'Average per week'
        },
        {
          icon: <Award className="w-5 h-5 text-green-600" />,
          label: 'Response Rate',
          value: `${insights.completionRate}%`,
          description: 'Meeting acceptance'
        },
        {
          icon: <Clock className="w-5 h-5 text-purple-600" />,
          label: 'Preferred Time',
          value: insights.mostPopularTimeSlot,
          description: 'Your preferred slot'
        },
        {
          icon: <Coffee className="w-5 h-5 text-green-600" />,
          label: 'This Month',
          value: meetingsThisWeekCount || 0,
          description: 'Meetings completed'
        }
      ];
    }
  };

  const metrics = getPerformanceMetrics();

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-800">Meeting Insights</h3>
        </div>
        <div className="text-xs text-gray-500">
          Based on {meetings.length} meetings
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        {metrics.map((metric, index) => (
          <div key={index} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <div className="flex items-center space-x-3 mb-2">
              {metric.icon}
              <span className="text-sm font-medium text-gray-700">{metric.label}</span>
            </div>
            <div className="text-xl font-bold text-gray-800 mb-1">{metric.value}</div>
            <div className="text-xs text-gray-500">{metric.description}</div>
          </div>
        ))}
      </div>

      {/* Quick Insights Summary */}
      <div className="border-t border-gray-200 pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          {userType === 'sales' ? 'Sales Performance' : 'Meeting Patterns'}
        </h4>
        <div className="space-y-2 text-sm text-gray-600">
          {userType === 'sales' ? (
            <>
              <p>• Your {insights.mostPopularTimeSlot.toLowerCase()} meetings have the highest success rate</p>
              <p>• {insights.mostProductiveDay} is your most productive day for scheduling</p>
              {insights.completionRate >= 80 && <p>• Excellent confirmation rate - keep it up! 🎉</p>}
            </>
          ) : (
            <>
              <p>• You typically prefer {insights.mostPopularTimeSlot.toLowerCase()} meetings</p>
              <p>• Most meeting requests come on {insights.mostProductiveDay}s</p>
              {insights.completionRate >= 70 && <p>• You're actively engaging with sales representatives</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingInsights;